import { useEffect, useState } from "react";
import Card from "components/card";
import {
  MdPerson,
  MdAssignment,
  MdCheckCircle,
  MdAccessTime,
  MdWarning,
} from "react-icons/md";
import Widget from "components/widget/Widget";
import LineChart from "components/charts/LineChart";
import BarChart from "components/charts/BarChart";
import PieChart from "components/charts/PieChart";
import { fetchWithAuth } from "services/api";

interface MonthlyTrend {
  month: string;
  total: number;
  completed: number;
  completion_rate: number;
}

interface QuestionnaireType {
  type: string;
  total: number;
  completed: number;
  completion_rate: number;
  avg_completion_time: {
    hours: number;
    days: number;
  };
}

interface DashboardStats {
  total_patients: number;
  assignments: {
    total: number;
    completed: number;
    pending: number;
    expired: number;
    completion_rate: number;
  };
  response_times: {
    average_hours: number;
    average_days: number;
  };
  questionnaires_per_patient: {
    average: number;
  };
  monthly_trends: MonthlyTrend[];
  questionnaire_types: QuestionnaireType[];
}

const Dashboard = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Here you would fetch the stats from your API
    const fetchStats = async () => {
      try {
        const response = await fetchWithAuth("/psychologist/stats");
        const data = response;
        if (data.success) {
          setStats(data.data);
        }
      } catch (error) {
        console.error("Error fetching stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading || !stats) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-lg font-semibold text-navy-700 dark:text-white">
          Loading...
        </div>
      </div>
    );
  }

  // Prepare data for monthly trends chart
  const monthlyTrendsData = {
    labels: stats.monthly_trends.map((trend) => {
      const [year, month] = trend.month.split("-");
      return `${month}/${year}`;
    }),
    datasets: [
      {
        label: "Total Asignados",
        data: stats.monthly_trends.map((trend) => trend.total),
        borderColor: "rgb(59, 130, 246)",
        tension: 0.4,
      },
      {
        label: "Completados",
        data: stats.monthly_trends.map((trend) => trend.completed),
        borderColor: "rgb(34, 197, 94)",
        tension: 0.4,
      },
    ],
  };

  // Prepare data for questionnaire types completion chart
  const questionnaireTypesData = {
    labels: stats.questionnaire_types.map((type) => type.type),
    datasets: [
      {
        label: "Tasa de Completitud (%)",
        data: stats.questionnaire_types.map((type) => type.completion_rate),
        backgroundColor: "rgba(59, 130, 246, 0.8)",
      },
    ],
  };

  // Prepare data for assignments status pie chart
  const assignmentsStatusData = {
    labels: ["Completados", "Pendientes", "Expirados"],
    datasets: [
      {
        data: [
          stats.assignments.completed,
          stats.assignments.pending,
          stats.assignments.expired,
        ],
        backgroundColor: [
          "rgb(34, 197, 94)",
          "rgb(234, 179, 8)",
          "rgb(239, 68, 68)",
        ],
      },
    ],
  };

  return (
    <div className="mt-3">
      {/* Main Stats Widgets */}
      <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <Widget
          icon={<MdPerson className="h-7 w-7" />}
          title="Total Pacientes"
          subtitle={stats.total_patients.toString()}
        />
        <Widget
          icon={<MdAssignment className="h-7 w-7" />}
          title="Cuestionarios Asignados"
          subtitle={stats.assignments.total.toString()}
        />
        <Widget
          icon={<MdCheckCircle className="h-7 w-7" />}
          title="Tasa de Completitud"
          subtitle={`${stats.assignments.completion_rate.toFixed(1)}%`}
        />
        <Widget
          icon={<MdAccessTime className="h-7 w-7" />}
          title="Tiempo Promedio de Respuesta"
          subtitle={`${stats.response_times.average_days} días`}
        />
      </div>

      {/* Charts Section */}
      <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        {/* Monthly Trends */}
        <Card extra="!p-5">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Tendencias Mensuales
            </h2>
            <p className="text-sm text-gray-600">
              Asignaciones y completitudes por mes
            </p>
          </div>
          <div className="h-[300px]">
            <LineChart chartData={monthlyTrendsData} />
          </div>
        </Card>

        {/* Questionnaire Types Completion */}
        <Card extra="!p-5">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Tasa de Completitud por Tipo
            </h2>
            <p className="text-sm text-gray-600">
              Porcentaje de completitud por cuestionario
            </p>
          </div>
          <div className="h-[300px]">
            <BarChart chartData={questionnaireTypesData} />
          </div>
        </Card>
      </div>

      {/* Additional Stats */}
      <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Assignments Status */}
        <Card extra="!p-5">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Estado de Asignaciones
            </h2>
            <p className="text-sm text-gray-600">
              Distribución de estados actuales
            </p>
          </div>
          <div className="h-[300px]">
            <PieChart chartData={assignmentsStatusData} />
          </div>
        </Card>

        {/* Questionnaire Details */}
        <Card extra="!p-5 lg:col-span-2">
          <div className="mb-4">
            <h2 className="text-lg font-bold text-navy-700 dark:text-white">
              Detalles por Cuestionario
            </h2>
            <p className="text-sm text-gray-600">
              Estadísticas detalladas por tipo de cuestionario
            </p>
          </div>
          <div className="max-h-[300px] overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 bg-white dark:bg-navy-800">
                <tr className="border-b border-gray-200">
                  <th className="py-3 text-left text-sm font-semibold text-gray-600">
                    Tipo
                  </th>
                  <th className="py-3 text-center text-sm font-semibold text-gray-600">
                    Total
                  </th>
                  <th className="py-3 text-center text-sm font-semibold text-gray-600">
                    Completados
                  </th>
                  <th className="py-3 text-center text-sm font-semibold text-gray-600">
                    Tasa
                  </th>
                  <th className="py-3 text-right text-sm font-semibold text-gray-600">
                    Tiempo Promedio
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.questionnaire_types.map((type) => (
                  <tr key={type.type} className="border-b border-gray-200">
                    <td className="py-2 text-sm font-medium text-navy-700 dark:text-white">
                      {type.type}
                    </td>
                    <td className="py-2 text-center text-sm text-gray-600">
                      {type.total}
                    </td>
                    <td className="py-2 text-center text-sm text-gray-600">
                      {type.completed}
                    </td>
                    <td className="py-2 text-center text-sm text-gray-600">
                      {type.completion_rate.toFixed(1)}%
                    </td>
                    <td className="py-2 text-right text-sm text-gray-600">
                      {type.avg_completion_time.days.toFixed(1)} días
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Dashboard;
